/**
 * S5 chart #4 (part 1) — "what just spiked, and why". A markets-style alert
 * feed of detected anomalies, each annotated with the attribution evidence:
 * nearby Gary uploads (the cut-through signal, green) and any coincident
 * exogenous calendar event (the confounder guard, amber). A Budget-day surge is
 * not cut-through — the annotation is what keeps the read honest.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { SalienceSpike } from "../../lib/api";
import { apiSalience, SALIENCE_AGENDA_LABELS } from "../../lib/api";
import { ArticleList, ArticleListSkeleton } from "./ArticleList";
import {
  EVENT_TYPE_LABELS,
  formatAgendaValue,
  GEO_LABELS,
  shortDate,
} from "./format";

interface SpikeFeedProps {
  spikes: SalienceSpike[];
  /** When true, link the term name to its detail page (overview context). */
  linkTerms?: boolean;
  termLabels?: Record<string, string>;
  emptyMessage?: string;
}

const SPIKE_FEED_INITIAL = 10;

export function SpikeFeed({
  spikes,
  linkTerms = false,
  termLabels = {},
  emptyMessage = "No spikes detected in the current window.",
}: SpikeFeedProps) {
  // The feed is newest-first and unbounded upstream (192 spikes incl. the 2019
  // BQ backfill), but the panel reads as "what just spiked" — so show the ten
  // most recent and let the reader expand to the full history on demand.
  const [showAll, setShowAll] = useState(false);

  if (spikes.length === 0) {
    return (
      <div className="rounded-lg border border-white/5 bg-terminal-panel/40 p-5 text-sm text-terminal-ink-muted">
        {emptyMessage}
      </div>
    );
  }

  const shown = showAll ? spikes : spikes.slice(0, SPIKE_FEED_INITIAL);
  const hidden = spikes.length - shown.length;

  return (
    <>
      <ul className="space-y-2">
        {shown.map((s, i) => (
          <SpikeRow
            key={`${s.termId}-${s.bucketDate}-${i}`}
            spike={s}
            linkTerm={linkTerms}
            termLabel={termLabels[s.termId] ?? s.termId}
          />
        ))}
      </ul>
      {spikes.length > SPIKE_FEED_INITIAL && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-2 w-full rounded-lg border border-white/10 bg-terminal-panel/60 px-3 py-2 text-[11px] font-semibold text-terminal-ink-muted transition-colors hover:text-terminal-ink"
        >
          {showAll
            ? "Show fewer"
            : `Show ${hidden} older spike${hidden === 1 ? "" : "s"}`}
        </button>
      )}
    </>
  );
}

function SpikeRow({
  spike: s,
  linkTerm,
  termLabel,
}: {
  spike: SalienceSpike;
  linkTerm: boolean;
  termLabel: string;
}) {
  // Media spikes have article-level provenance in salienceArticles (captured
  // from GDELT GKG via BigQuery for that exact day) — expandable on demand.
  const [showStories, setShowStories] = useState(false);
  // Agenda-aware so a search-agenda spike renders its 0–100 Trends index as a
  // bare "62", not "62%" (formatShare would mislabel the index as a percent).
  const value = formatAgendaValue(s.value, s.agenda);
  const baseline =
    s.metric === "share"
      ? formatAgendaValue(s.baselineMean, s.agenda)
      : (s.baselineMean ?? 0).toFixed(1);

  return (
    <li className="rounded-lg border border-white/10 bg-terminal-panel p-3.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-xs tabular-nums text-terminal-ink-muted">
            {shortDate(s.bucketDate)}
          </span>
          {linkTerm ? (
            <Link
              to={`/salience/terms/${s.termId}`}
              className="text-sm font-semibold text-terminal-ink hover:underline"
            >
              {termLabel}
            </Link>
          ) : (
            <span className="text-sm font-semibold text-terminal-ink">
              {termLabel}
            </span>
          )}
          <span className="text-[11px] uppercase tracking-wider text-terminal-ink-muted">
            {SALIENCE_AGENDA_LABELS[s.agenda]} · {GEO_LABELS[s.geo] ?? s.geo}
          </span>
        </div>
        <div className="flex items-center gap-2 font-mono text-xs tabular-nums">
          <span className="font-semibold text-terminal-ink">{value}</span>
          <span className="text-terminal-ink-muted">vs {baseline} base</span>
          {s.zScore != null && (
            <span className="rounded bg-down/15 px-1.5 py-0.5 font-semibold text-down">
              z {s.zScore.toFixed(1)}
            </span>
          )}
        </div>
      </div>

      {(s.nearGaryUpload || s.calendarHitCount > 0) && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {s.garyUploads.slice(0, 2).map((u) => (
            <span
              key={u.video_id}
              className="inline-flex max-w-full items-center gap-1 rounded bg-up/15 px-1.5 py-0.5 text-[11px] text-up"
              title={`${u.title} (${u.published_at})`}
            >
              <span aria-hidden>▶</span>
              <span className="truncate">{u.title}</span>
            </span>
          ))}
          {s.garyUploadCount > 2 && (
            <span className="rounded bg-up/15 px-1.5 py-0.5 text-[11px] text-up">
              +{s.garyUploadCount - 2} more uploads
            </span>
          )}
          {s.calendarHits.map((c, i) => (
            <span
              key={`c-${i}`}
              className="inline-flex items-center gap-1 rounded bg-[#f5a524]/15 px-1.5 py-0.5 text-[11px] text-[#f5a524]"
              title={EVENT_TYPE_LABELS[c.event_type] ?? c.event_type}
            >
              <span aria-hidden>◆</span>
              {c.label}
            </span>
          ))}
        </div>
      )}

      {s.agenda === "media" && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowStories((v) => !v)}
            className="text-[11px] font-semibold text-terminal-ink-muted transition-colors hover:text-terminal-ink"
          >
            {showStories ? "▾ Hide the coverage" : "▸ What was the coverage?"}
          </button>
          {showStories && <SpikeStories termId={s.termId} day={s.bucketDate} />}
        </div>
      )}
    </li>
  );
}

/** The day's captured headlines behind a media spike (GKG via BigQuery; titles
 * reconstructed from URL slugs — GDELT stores no titles historically). */
function SpikeStories({ termId, day }: { termId: string; day: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["salience", "articles", termId, day],
    queryFn: () => apiSalience.articles({ term: termId, from: day, to: day }),
  });

  if (isLoading) {
    return (
      <div className="mt-1.5">
        <ArticleListSkeleton />
      </div>
    );
  }
  const articles = data ?? [];
  if (articles.length === 0) {
    return (
      <div className="mt-1.5 text-[11px] text-terminal-ink-muted">
        No captured articles for this day.
      </div>
    );
  }
  return (
    <div className="mt-1.5">
      <ArticleList articles={articles} />
    </div>
  );
}
